/**
 * Notification Priority Icon
 *
 * Maps notification category to an appropriate Lucide icon and color.
 * Used inside NotificationItem for visual categorization.
 *
 * @module components/notifications/NotificationPriorityIcon
 */

import React from 'react';
import {
  Megaphone,
  DollarSign,
  Shield,
  Info,
  UserPlus,
  Sparkles,
} from 'lucide-react';
import type { NotificationCategory } from '../../entities/notification/types';

interface NotificationPriorityIconProps {
  category: NotificationCategory;
  className?: string;
}

/** Category to icon + color mapping */
const CATEGORY_CONFIG: Record<
  NotificationCategory,
  {
    icon: React.ElementType;
    bgColor: string;
    iconColor: string;
  }
> = {
  CAMPAIGNS: {
    icon: Megaphone,
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
  },
  TRANSACTIONS: {
    icon: DollarSign,
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-600 dark:text-blue-400',
  },
  SECURITY: {
    icon: Shield,
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    iconColor: 'text-red-600 dark:text-red-400',
  },
  SYSTEM: {
    icon: Info,
    bgColor: 'bg-gray-100 dark:bg-gray-800/50',
    iconColor: 'text-gray-600 dark:text-gray-400',
  },
  ONBOARDING: {
    icon: UserPlus,
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    iconColor: 'text-amber-600 dark:text-amber-400',
  },
  MARKETING: {
    icon: Sparkles,
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    iconColor: 'text-purple-600 dark:text-purple-400',
  },
};

/**
 * Renders a category-colored icon inside a circular badge
 */
const NotificationPriorityIcon: React.FC<NotificationPriorityIconProps> = ({
  category,
  className = '',
}) => {
  const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.SYSTEM;
  const Icon = config.icon;

  return (
    <div
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${config.bgColor} ${className}`}
    >
      <Icon className={`h-4 w-4 ${config.iconColor}`} />
    </div>
  );
};

export default NotificationPriorityIcon;
